<template>
  <div>
    <slot name="product-alert"></slot>
    <p-product-header
      :product-id="product._id"
      :is-authenticated="isAuthenticated"
      :cooperative-name="cooperativeName"
      :is-front-agri="isFrontAgri"
      :is-aladin="isAladin"
      @go-to-product-search="goToProductSearch"
    >
      <template #header-supplier>
        <slot name="header-supplier"></slot>
      </template>
      <template #header-market-authorization>
        <slot name="header-market-authorization"></slot>
      </template>
      <template #header-details>
        <slot name="header-details"></slot>
      </template>
    </p-product-header>
    <slot name="header-regulatory-display"></slot>
    <p-product-menu
      class="product-detail__menu"
      :class="{ 'has-no-shipping-ways': !(hasOffers || !isAuthenticated) }"
      :product-id="product._id"
      :cooperative-name="cooperativeName"
      :offer-list-target="'#product-detail__offer-list'"
      :product-detail-target="'#product-detail'"
      :productCooperativeReviewTarget="'#product-review'"
      :product-documentation-target="'#product-documentation'"
      :product-usage-target="'#product-usages'"
      :calculator-target="'#calculator'"
      :stocks-target="'#stocks'"
    />
    <p-product-logged-out v-if="!isAuthenticated" :product="product" />
    <p-realtime-stocks-section
      v-if="isRealtimeStocksEnabled"
      id="stocks"
      :variant-stocks-infos="stocksInfos"
      :is-loading="isRealtimeStocksLoading"
      @shortcut="scrollToVariantOffers"
    />
    <p-variant-block
      v-if="isProductAvailable"
      :product-id="product._id"
      :header-show="isCalculableProduct"
      :header-sticky="hasPositiveConvertResult"
    >
      <template #header>
        <p-product-calculator-summary :product-id="product._id" scroll-target="#calculator" />
      </template>
    </p-variant-block>
    <p-calculator v-if="isCalculableProduct" id="calculator" :product-id="product._id" />
    <div v-if="banner" class="discount-banner">
      <adn-discount-banner
        v-observe-visibility="{
          callback: bannerVisibilityChanged(),
          throttle: 1000,
          intersection: {
            threshold: 0.75,
          },
          once: true,
        }"
        :description="banner.description"
        :desktop-image="banner.desktopImage"
        :is-small="banner.isSmall"
        :link="banner.link"
        :mobile-image="banner.mobileImage"
        class="home__banner"
        data-test-banner-0
        @click.capture="onBannerClick(banner)"
      />
    </div>
    <div v-if="displayAssociatedProducts" class="associated-products-gallery">
      <p-associated-product-carousel
        id="associated-products-gallery"
        :title="i18n._('Les produits suggérés avec {productName}', { productName: product.name })"
        :products="sellableAssociatedProducts"
        :product-id="product._id"
        :product-name="product.name"
        :max-nb-visible-elements="6"
        :thumbnail-options="{ isAnAssociatedProduct: true, isAuthenticated }"
        click-event-name="click_suggested_product"
        :click-event-action-field-list="i18n._('Cross-sell - produit associé depuis la Page Produit')"
        view-event-name="view_suggested_product"
        class="associated-products-gallery__carousel"
      />
    </div>
    <div
      v-if="!displayAssociatedProducts && isAuthenticated && similarProducts.length > 0"
      class="similar-products-gallery"
    >
      <p-associated-product-carousel
        id="similar-products-gallery"
        :title="i18n._('Produits similaires')"
        :products="similarProducts"
        :product-id="product._id"
        :product-name="product.name"
        :max-nb-visible-elements="6"
        :thumbnail-options="{ isAnAssociatedProduct: true, isAuthenticated }"
        class="associated-products-gallery__carousel"
      />
    </div>
    <div v-if="frequentlyBoughtTogetherProducts.length > 0" class="frequently-bought-together-products-gallery">
      <p-associated-product-carousel
        id="frequently-bought-together-products-gallery"
        :title="i18n._('Produits fréquemment achetés ensemble')"
        :products="frequentlyBoughtTogetherProducts"
        :product-id="product._id"
        :product-name="product.name"
        :max-nb-visible-elements="6"
        :thumbnail-options="{ isAnAssociatedProduct: true, isAuthenticated }"
        class="associated-products-gallery__carousel"
      />
    </div>
    <div v-if="displayOtherAssociatedProducts" class="associated-products-gallery">
      <p-associated-product-carousel
        id="supplier-related-products-gallery"
        :title="i18n._('Découvrir les autres produits {productSupplier}', { productSupplier: product.supplier })"
        :products="supplierRelatedProducts"
        :product-id="product._id"
        :product-name="product.name"
        :max-nb-visible-elements="6"
        :thumbnail-options="{ isAnAssociatedProduct: true, isAuthenticated }"
        click-event-name="click_recommended_brand_product"
        click-event-action-field-list="Cross-sell - produit associé au fournisseur depuis la Page Produit"
        view-event-name="view_recommended_brand_product"
        :cta-url="supplierRelatedProductCount > 6 ? supplierRelatedSearchUrl : undefined"
        :cta-label="
          supplierRelatedProductCount > 6 ? `Voir les ${supplierRelatedProductCount + 1} produits` : undefined
        "
        class="associated-products-gallery__carousel"
      />
    </div>
    <div class="product-detail__body">
      <div v-if="!isAuthenticated && !isEpisens" class="product-detail__aladin-benefits">
        <p-aladin-benefits />
      </div>
      <div v-if="isFrontAgri && isAladin" class="product-detail__technical-sale">
        <p-product-technical-sale
          :is-authenticated="isAuthenticated"
          :cooperative-name="cooperativeName"
          :technical-sales="technicalSales"
          @login="login()"
        />
      </div>
      <slot name="default"></slot>
    </div>
    <slot name="legal-mention"></slot>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, toRefs, inject, type PropType } from 'vue'
import { i18n } from '@b2ag/locale'

import { useProduct } from '@b2ag/catalog/src/useProduct'
import { useRouter } from 'vue-router'
import { provideCalculator } from '@b2ag/calculator/src/composables/useCalculator'
import { currentAppBrand, isAladin, isEpisens } from '@b2ag/app-brands'
import { useAuthService } from '@b2ag/auth'

import { buildCallerId } from '@b2ag/utils/src/caller-id'
import VueScrollTo from 'vue-scrollto'
import PRealtimeStocksSection from '@b2ag/realtime-stocks/src/PRealtimeStocksSection.vue'
import PCalculator from '@b2ag/calculator/src/components/PCalculator/PCalculator.vue'
import { useRealtimeStocks } from '@b2ag/realtime-stocks/src/useRealtimeStocks'

import PAladinBenefits from '../PAladinBenefits/PAladinBenefits.vue'
import PVariantBlock from '../variant/PVariantBlock/PVariantBlock.vue'
import { hasOffers as hasOffersFn } from '../../services/product.business'
import PProductCalculatorSummary from '../PProductCalculatorSummary/PProductCalculatorSummary.vue'
import PProductMenu from '../PProductMenu/PProductMenu.vue'
import PProductHeader from '../PProductHeader/PProductHeader.vue'
import PProductLoggedOut from '../PProductLoggedOut/PProductLoggedOut.vue'
import PProductTechnicalSale from '../PProductTechnicalSale/PProductTechnicalSale.vue'
import PAssociatedProductCarousel from '../PAssociatedProductCarousel/PAssociatedProductCarousel.vue'
import { useSellableAssociatedProducts, useSupplierRelatedSearchUrl } from './helpers'
import type { Product, TechnicalSale } from '../typings'
import { getAppContext } from '@b2ag/micro-front-tools'
import AdnDiscountBanner from '@b2ag/design-system/src/components/AdnDiscountBanner/AdnDiscountBanner.vue'
import { ObserveVisibility } from 'vue3-observe-visibility'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import type { Banner } from '@b2ag/contexts/marketing/src/domain/blocs/Banner'

export default defineComponent({
  name: 'PProductLayout',
  components: {
    AdnDiscountBanner,
    PRealtimeStocksSection,
    PCalculator,
    PProductCalculatorSummary,
    PProductTechnicalSale,
    PProductMenu,
    PAladinBenefits,
    PVariantBlock,
    PProductHeader,
    PProductLoggedOut,
    PAssociatedProductCarousel,
  },
  directives: {
    ObserveVisibility,
  },
  props: {
    product: {
      type: Object as PropType<Product>,
      required: true,
    },
    supplierRelatedProducts: {
      type: Array as PropType<Array<Product>>,
      default: () => [],
    },
    similarProducts: {
      type: Array as PropType<Array<Product>>,
      default: () => [],
    },
    frequentlyBoughtTogetherProducts: {
      type: Array as PropType<Array<Product>>,
      default: () => [],
    },
    supplierRelatedProductCount: {
      type: Number,
      default: 0,
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
    cooperativeName: {
      type: String,
      default: '',
    },
    technicalSales: {
      type: Array as PropType<Array<TechnicalSale>>,
      default: () => [],
    },
    banner: {
      type: Object as PropType<Banner>,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const { product, isAuthenticated, supplierRelatedProducts, banner } = toRefs(props)

    const router = useRouter()
    const { isFrontAgri } = getAppContext()
    const { isCalculableProduct } = useProduct(product.value._id)
    const { hasPositiveConvertResult } = provideCalculator(product.value._id)

    const { stocksInfos, isRealtimeStocksLoading, isRealtimeStocksEnabled } = useRealtimeStocks(
      product.value.variants,
      {
        callerId: buildCallerId(),
        accessTokenSource: () => useAuthService().getAccessToken(),
      },
    )

    const scrollToVariantOffers = (variant) => {
      VueScrollTo.scrollTo(`#${variant.id}`, 300, {
        offset: -180,
      })
    }

    const hasOffers = computed(() => hasOffersFn(product.value))
    const isProductAvailable = computed(() => isAuthenticated.value && product.value.isCooperativeProduct)

    const sellableAssociatedProducts = computed(() => useSellableAssociatedProducts(product.value))

    const hasSellableAssociatedProducts = computed(() => sellableAssociatedProducts.value.length > 0)

    const displayAssociatedProducts = computed(() => isProductAvailable.value && hasSellableAssociatedProducts.value)
    const displayOtherAssociatedProducts = computed(() => supplierRelatedProducts.value.length > 0 && isAladin)

    const getSlugFromCategories = inject('getSlugFromCategories') as (categories: string[]) => string
    const supplierRelatedSearchUrl = computed(() =>
      useSupplierRelatedSearchUrl(product.value, router, getSlugFromCategories),
    )

    const login = () => emit('login')
    const goToProductSearch = (goToProductSearchArgs) => emit('goToProductSearch', goToProductSearchArgs)

    const onBannerClick = (theBanner) => {
      const payload = {
        content_title: theBanner.contentName,
      }
      useAnalyticService().trackEvent('click_banner', payload)
    }

    const bannerVisibilityChanged = () => {
      return function bannerVisibilyHandler(isVisible) {
        if (isVisible && banner.value) {
          const payload = {
            content_title: banner.value.contentName,
          }
          useAnalyticService().trackEvent('view_banner', payload)
        }
      }
    }

    return {
      stocksInfos,
      currentAppBrand,
      isCalculableProduct,
      isFrontAgri,
      hasPositiveConvertResult,
      isAladin,
      isEpisens,
      isRealtimeStocksEnabled,
      isRealtimeStocksLoading,
      scrollToVariantOffers,
      hasOffers,
      isProductAvailable,
      sellableAssociatedProducts,
      hasSellableAssociatedProducts,
      displayAssociatedProducts,
      displayOtherAssociatedProducts,
      supplierRelatedSearchUrl,
      login,
      goToProductSearch,
      onBannerClick,
      bannerVisibilityChanged,
      i18n,
    }
  },
})
</script>

<style scoped lang="scss">
.product-detail__cooperative-insight {
  display: flex;
  margin-bottom: 0.5rem;

  &-icon {
    margin-right: 0.5rem;

    &-highlighted {
      font-size: 3rem;
      color: var(--color-success);
    }

    &-exclusivity {
      font-size: 3rem;
      color: var(--color-highlight);
    }
  }

  &-label {
    margin-top: 0.2rem;

    &-cooperative {
      @media (min-width: $breakpoint-xl) {
        white-space: nowrap;
      }
    }
  }

  + .product-detail__cooperative-insight {
    @media screen and (min-width: $breakpoint-lg) {
      margin-left: 1.5rem;
    }
  }
}

.product-detail__summary-container {
  width: 100%;
  margin: 0;
  padding: 0 20px;

  @media (min-width: $breakpoint-md) {
    padding: 0;
  }
}

.product-detail__summary-heading {
  margin: 10px 0 15px;
}

.product-detail__title {
  color: var(--color-black);
  display: block;
  margin: 0;
  font-weight: bold;
  text-transform: uppercase;
  min-height: 1em;
  font-size: 28px;
  word-wrap: break-word;
}

.product-detail__body {
  display: flex;
  flex-direction: column;
  margin: 0 0 20px;

  @media (min-width: $breakpoint-md) {
    margin: 0;
  }
}

.product-detail__menu {
  &.has-no-shipping-ways {
    margin-top: 20rem;
  }

  @media (min-width: $breakpoint-md) {
    &,
    &.has-no-shipping-ways {
      margin-top: 12rem;
    }
  }

  @media screen and (min-width: $breakpoint-lg) {
    margin-top: 6rem;

    &.has-no-shipping-ways {
      margin-top: 12rem;
    }
  }

  @media screen and (min-width: $breakpoint-xl) {
    margin-top: 2rem;
  }
}

.product-detail__no-product {
  grid-column: 1;

  @media (min-width: $breakpoint-md) {
    grid-column: 1 / 3;
  }
}

.product-detail__aladin-benefits {
  margin-top: var(--spacing-3);
  background: white;
}

.product-detail__see-offers-btn {
  display: none;
  margin-top: 20px;
  cursor: pointer;
  width: 170px;
  padding: 10px 20px;
  background: var(--color-main-background);
  box-shadow: 1px 2px 1px var(--color-inactive-light);
  border-radius: var(--border-radius-1);
  color: var(--color-main);
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 30px;

  @media (min-width: $breakpoint-md) {
    display: block;
  }
}

.product-detail__icon-arrow-down {
  margin-right: 10px;
}

.product-detail__summary_not_authenticated {
  color: var(--color-secondary);
  margin-top: 0.75em;
}

.product-detail-asterisk {
  margin-top: 1em;
  color: var(--color-text-light);

  p {
    padding-top: 1em;
  }
}

.product-detail__shipping-ways {
  margin-left: 2rem;
  line-height: 2.2rem;
  margin-top: 17.5rem;

  @media (min-width: $breakpoint-md) {
    margin-left: 0;
    margin-top: 0;
  }

  @media (min-width: $breakpoint-lg) {
    margin-left: 0;
    margin-top: 0;
  }

  @media screen and (min-width: $breakpoint-xl) {
    margin-left: 4rem;
    margin-top: 1rem;
  }
}

.calculator-header {
  display: grid;
  gap: 1rem;
  grid-template-columns: auto;
  grid-template-rows: auto;
  grid-template-areas: 'convert-result';

  @media (min-width: $breakpoint-md) {
    grid-template-columns: 3em 1fr 1fr 3em;
    grid-template-rows: auto;
    grid-template-areas: '. convert-result see-calculator .';
  }
}

.convert-result {
  grid-area: convert-result;
  margin: auto;

  @media (min-width: $breakpoint-md) {
    margin: unset;
  }
}
.discount-banner {
  margin: var(--spacing-4) 0;
}

.similar-products-gallery {
  margin-top: var(--spacing-4);
  margin-bottom: var(--spacing-2);
}

.frequently-bought-together-products-gallery {
  margin-top: var(--spacing-4);
  margin-bottom: var(--spacing-2);
  background: var(--color-border-light);
  border-radius: var(--border-radius-2);

  @media (max-width: $breakpoint-md) {
    margin: var(--spacing-3);
  }
}
</style>
